import { trustedKeyDeal, hexShard, hexPubShard } from '@fiatjaf/promenade-trusted-dealer'
import { argon2id } from '@noble/hashes/argon2.js'
import { sha256 } from '@noble/hashes/sha2.js'
import { bytesToHex } from '@noble/hashes/utils.js'
import { pool } from '@nostr/gadgets/global'
import { loadRelayList } from '@nostr/gadgets/lists'
import { finalizeEvent, generateSecretKey } from '@nostr/tools/wasm'
import { getDefaultStore } from 'jotai'
import { pomegranateAuthAtom } from '@/atoms/pomegranate'

const utf8 = new TextEncoder()

const KIND_SETUP_ANNOUNCEMENT = 16440

function findTokenEmail(token: string): string {
  try {
    const parsed = JSON.parse(atob(token)) as { tags?: string[][] }
    const emailTag = Array.isArray(parsed?.tags)
      ? parsed.tags.find(
          (t: string[]) => Array.isArray(t) && t[0] === 'email' && typeof t[1] === 'string'
        )
      : null
    return emailTag?.[1] ?? ''
  } catch {
    return ''
  }
}

async function searchForActualCentralURLAnnounced(email: string): Promise<string | null> {
  if (!email) return null

  const hash = bytesToHex(argon2id(utf8.encode(email), 'pomegranate', { t: 1, m: 65536, p: 4 }))

  const results = await pool.querySync(
    window.hallway.universe.bigRelayUrls,
    { kinds: [KIND_SETUP_ANNOUNCEMENT], '#m': [hash], limit: 1 },
    {
      maxWait: 5000
    }
  )

  if (results.length) {
    const centralTag = results[0].tags?.find(
      (t: string[]) => Array.isArray(t) && t[0] === 'central' && typeof t[1] === 'string'
    )
    if (centralTag?.[1]) {
      return centralTag[1]
    }
  }

  return null
}

async function authenticateWithGoogle(central: string): Promise<string> {
  const popup = window.open(`${central}/login/google`, 'PomegranateOAuth', 'width=600,height=600')
  if (!popup) throw new Error('Popup blocked')

  return new Promise<string>((resolve, reject) => {
    let settled = false
    const monitor = window.setInterval(() => {
      if (settled || !popup.closed) return
      finish(null, new Error('Login cancelled'))
    }, 250)
    function finish(value: string | null, err?: Error) {
      if (settled) return
      settled = true
      window.clearInterval(monitor)
      window.removeEventListener('message', handler)
      if (err) reject(err)
      else resolve(value!)
    }
    function handler(event: MessageEvent) {
      if (
        event.origin !== central ||
        (event.source as Window) !== popup ||
        !(event.data as any)?.token
      ) {
        return
      }
      popup.close()
      finish((event.data as any).token as string)
    }
    window.addEventListener('message', handler)
  })
}

export const TRY_AGAIN = new Error('try again please')

export async function googleLogin(central: string): Promise<string> {
  const token = await authenticateWithGoogle(central)
  const email = findTokenEmail(token)

  let account = await tryFetchAccount(central, token)
  if (!account) {
    const actualCentral = await searchForActualCentralURLAnnounced(email)
    if (actualCentral && actualCentral !== central) {
      throw { type: TRY_AGAIN, central: actualCentral }
    }
  }

  if (!account) {
    const operators = window.hallway.universe.pomegranateOperators!
    await createAccount(central, token, operators, email)
    account = await tryFetchAccount(central, token)
    if (!account) throw new Error('Account creation failed')
  }

  getDefaultStore().set(pomegranateAuthAtom, { token, centralURL: central })

  return fetchDefaultBunkerUri(central, token)
}

async function tryFetchAccount(central: string, token: string) {
  const resp = await fetch(`${central}/account`, {
    headers: { Authorization: 'Token ' + token }
  })
  if (resp.status === 404) return null
  if (!resp.ok) throw new Error(`central /account returned ${resp.status}`)
  return resp.json()
}

async function createAccount(central: string, token: string, operators: string[], email: string) {
  const threshold = Math.ceil((operators.length * 7) / 12)
  const session = crypto.randomUUID()

  const secretKey = generateSecretKey()
  const bytes = Array.from(secretKey) as number[]
  const masterSkBignum: bigint = bytes.reduce(
    (acc: bigint, byte: number) => (acc << 8n) + BigInt(byte),
    0n
  )
  const { shards } = trustedKeyDeal(masterSkBignum, threshold, operators.length)

  const regEvent = finalizeEvent(
    {
      kind: 20445,
      created_at: Math.floor(Date.now() / 1000),
      tags: [
        ['threshold', String(threshold)],
        ...operators.map((op, i) => ['operator', op, hexPubShard(shards[i].pubShard)])
      ],
      content: ''
    },
    secretKey
  )
  const regResp = await fetch(`${central}/register`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: 'Token ' + token,
      'X-Pomegranate-Session': session
    },
    body: JSON.stringify(regEvent)
  })
  if (!regResp.ok) {
    const body = await regResp.text().catch(() => '')
    throw new Error(`central /register failed (${regResp.status}): ${body.slice(0, 200)}`)
  }

  for (let i = 0; i < operators.length; i++) {
    const op = operators[i]
    const event = finalizeEvent(
      {
        kind: 20444,
        created_at: Math.floor(Date.now() / 1000),
        tags: [
          ['central', central],
          ['email', email]
        ],
        content: hexShard(shards[i])
      },
      secretKey
    )
    const opResp = await fetch(`${op}/po/register`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Pomegranate-Operator-Token': bytesToHex(sha256(utf8.encode(session + ':' + op)))
      },
      body: JSON.stringify(event)
    })
    if (!opResp.ok) {
      const body = await opResp.text().catch(() => '')
      throw new Error(`${op} /register failed (${opResp.status}): ${body.slice(0, 200)}`)
    }
  }

  const hash = bytesToHex(argon2id(utf8.encode(email), 'pomegranate', { t: 1, m: 65536, p: 4 }))
  const event = finalizeEvent(
    {
      kind: KIND_SETUP_ANNOUNCEMENT,
      created_at: Math.floor(Date.now() / 1000),
      tags: [
        ['m', hash],
        ['central', central]
      ],
      content: ''
    },
    secretKey
  )

  await Promise.allSettled(
    pool.publish(
      [
        ...window.hallway.universe.bigRelayUrls,
        ...(await loadRelayList(event.pubkey)).items.filter((r) => r.write).map((r) => r.url)
      ],
      event
    )
  )
}

async function fetchDefaultBunkerUri(central: string, token: string): Promise<string> {
  const list = async () => {
    const resp = await fetch(`${central}/profiles`, {
      headers: { Authorization: 'Token ' + token }
    })
    if (!resp.ok) throw new Error(`central /profiles returned ${resp.status}`)
    return resp.json()
  }

  let profiles = await list()
  if (!Array.isArray(profiles)) profiles = []
  let profile = profiles.find((p: any) => p?.name === 'default')

  if (!profile) {
    const createResp = await fetch(`${central}/profiles`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: 'Token ' + token
      },
      body: JSON.stringify({ name: 'default' })
    })
    if (!createResp.ok) throw new Error('profile creation failed')
    profiles = await list()
    if (!Array.isArray(profiles)) profiles = []
    profile = profiles.find((p: any) => p?.name === 'default')
  }

  if (!profile?.handler_pubkey) {
    throw new Error('central did not return a default profile with handler_pubkey')
  }

  const relay = central.replace(/^http/, 'ws')
  return `bunker://${profile.handler_pubkey}?relay=${encodeURIComponent(relay)}`
}
